import { TmdxDatepicker, TmdxSelect, TmdxTimeInput } from '@ds';
import { getPerfusionTime } from '@features/components/ClinicalData/utils';
import { fromZonedTime } from 'date-fns-tz';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { parseDate, parseTime } from '@internationalized/date';
import { isValid } from 'date-fns';
import { TIMEZONES_WITH_UTC_SELECT_LIST } from '@/constants/form.constants';
import { renderSelectItem } from '@/utils/helpers';

type OCSTimeFieldsProps = {
  heading: string;
  subHeading?: string;
  dateFieldName: string;
  timeFieldName: string;
  timezoneFieldName: string;
  ocsStartTime?: string;
  compactView?: boolean;
  showPerfusionTime?: boolean;
  footerText?: string;
};

const defaultFooterText = 'At time of reading';

export const OCSTimeFields = ({
  heading,
  subHeading,
  dateFieldName,
  timeFieldName,
  timezoneFieldName,
  ocsStartTime,
  footerText = defaultFooterText,
  compactView,
  showPerfusionTime,
}: OCSTimeFieldsProps) => {
  const { control, trigger } = useFormContext();
  const [dateField, timeField, timezoneField] = useWatch({
    control,
    name: [dateFieldName, timeFieldName, timezoneFieldName],
  });

  const offOcsDateTime = fromZonedTime(new Date(`${dateField} ${timeField}`), timezoneField);

  const perfusionTime =
    ocsStartTime && isValid(offOcsDateTime) ? getPerfusionTime(offOcsDateTime, ocsStartTime) : '--:--';

  return (
    <div className="flex flex-col flex-1 gap-6">
      <div className="font-semibold text-hm text-base-10">{heading}</div>
      <div className="flex flex-col gap-4">
        {subHeading && <div className="text-l-sb">{subHeading}</div>}
        <Controller
          name={dateFieldName}
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <TmdxDatepicker
              aria-label="On OCS date"
              isInvalid={invalid}
              errorMessage={error?.message}
              label={null}
              value={value ? parseDate(value) : null}
              onChange={(v) => {
                onChange(v ? v.toString() : v);
              }}
              {...field}
            />
          )}
        />
        <div className={`flex gap-4 ${compactView ? '' : 'flex-col'}`}>
          <Controller
            name={timeFieldName}
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <TmdxTimeInput
                className="flex-1"
                aria-label="On OCS time"
                isInvalid={invalid}
                errorMessage={error?.message}
                hideTimeZone
                label={null}
                hourCycle={24}
                value={value ? parseTime(value) : null}
                onChange={(v) => {
                  onChange(v ? v.toString() : v);
                  trigger(dateFieldName);
                }}
                {...field}
              />
            )}
          />
          <Controller
            name={timezoneFieldName}
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <TmdxSelect
                className="flex-1"
                placeholder="Choose an option"
                selectedKeys={[value]}
                isInvalid={invalid}
                errorMessage={error?.message}
                disallowEmptySelection
                items={TIMEZONES_WITH_UTC_SELECT_LIST}
                onChange={(e) => {
                  onChange(e);
                  trigger(dateFieldName);
                }}
                {...field}
              >
                {renderSelectItem}
              </TmdxSelect>
            )}
          />
        </div>
        <div className="text-m-r text-base-20">{footerText}</div>
      </div>
      {showPerfusionTime && (
        <div className="flex flex-col gap-4">
          <div className="text-l-sb">Perfusion duration</div>
          <div className="h-8 w-full bg-base-60 px-3 flex items-center rounded-lg text-base-20 text-m-r truncate">
            {perfusionTime}
          </div>
        </div>
      )}
    </div>
  );
};
